import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import Spinner from "react-bootstrap/Spinner";
import {
    MainContainer,
    ContainerBody,
    TarjetaAtencion,
    NombreTratamiento,
    DientesEtiqueta
} from "./styles"; // Reusing styles, might need adaptation
import { fetchAllPrestacionesHistory } from "../../services/firebase/prestaciones";
import { fetchSesionesRealizadas } from "../../services/firebase/sesiones";
import { fetchAtencionesHistory } from "../../services/firebase/atenciones";
import { Paper, Tabs, Tab, Box, Typography, Card, CardContent, Chip, Divider } from "@material-ui/core";
import DescriptionIcon from '@material-ui/icons/Description';
import EventNoteIcon from '@material-ui/icons/EventNote';
import ViewWeekIcon from '@material-ui/icons/ViewWeek';
import moment from "moment";

// Helper for dates
const formatDate = (dateObj) => {
    if (!dateObj) return "";
    const date = dateObj.toDate ? dateObj.toDate() : new Date(dateObj);
    return moment(date).format("DD/MM/YYYY HH:mm");
};

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export const HistoriaClinica = () => {
    const paciente = useSelector((state) => state.paciente);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabHasData, setTabHasData] = useState(true); // Control empty states

    // Raw Data
    const [atenciones, setAtenciones] = useState([]);
    const [prestaciones, setPrestaciones] = useState([]);
    const [sesiones, setSesiones] = useState([]);

    // Processed Data
    const [viewMode, setViewMode] = useState(0); // 0: Atenciones, 1: Prestaciones, 2: Dientes

    useEffect(() => {
        loadData();
    }, [paciente.id]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [atencionesData, prestacionesData, sesionesData] = await Promise.all([
                fetchAtencionesHistory(paciente.id, paciente.empresaId),
                fetchAllPrestacionesHistory(paciente.id, paciente.empresaId),
                fetchSesionesRealizadas(paciente.id, paciente.empresaId)
            ]);
            setAtenciones(atencionesData);
            setPrestaciones(prestacionesData);
            setSesiones(sesionesData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeTab = (event, newValue) => {
        setViewMode(newValue);
    };

    // --- RENDERERS ---

    const renderByAtencion = () => {
        if (atenciones.length === 0) return <Typography>No hay historial de atenciones registradas.</Typography>;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {atenciones.map(atencion => {
                    // Find sessions for this attention
                    const atencionSesiones = sesiones.filter(s => s.atencionId === atencion.id);

                    return (
                        <Card key={atencion.id} elevation={3}>
                            <CardContent>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <Typography variant="h6" style={{ color: '#0c8de3', fontWeight: 'bold' }}>
                                        {formatDate(atencion.fechaInicio)}
                                    </Typography>
                                    <Chip
                                        label={atencion.estado}
                                        style={{
                                            backgroundColor: atencion.estado === 'ANULADA' ? '#e57373' : (atencion.estado === 'ABIERTA' ? '#81c784' : '#e0e0e0'),
                                            color: atencion.estado === 'ANULADA' ? 'white' : 'black'
                                        }}
                                        size="small"
                                    />
                                </div>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Especialista: {atencion.especialistaId}
                                </Typography>
                                <Divider style={{ margin: '10px 0' }} />

                                {atencionSesiones.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {atencionSesiones.map(sesion => {
                                            const prestacion = prestaciones.find(p => p.id === sesion.prestacionId);
                                            return (
                                                <div key={sesion.id} style={{ padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '5px', borderLeft: '4px solid #67D2DC' }}>
                                                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                                        {prestacion ? `${prestacion.tratamiento} ${prestacion.tipo || ''}` : 'Tratamiento desconocido'}
                                                    </Typography>
                                                    {prestacion && prestacion.dientes && prestacion.dientes.length > 0 && (
                                                        <Typography variant="caption" display="block">
                                                            Dientes: {prestacion.dientes.join(', ')}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="body2" style={{ marginTop: '5px' }}>
                                                        {sesion.nota || "Sin nota clínica"}
                                                    </Typography>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <Typography variant="body2" style={{ fontStyle: 'italic', color: '#999' }}>
                                        No se registraron procedimientos en esta atención.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        );
    };

    const renderByPrestacion = () => {
        const filteredPrestaciones = prestaciones.filter(p => p.estado === 'ACTIVO' || p.estado === 'TERMINADO');
        if (filteredPrestaciones.length === 0) return <Typography>No hay tratamientos en curso o terminados registrados.</Typography>;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {filteredPrestaciones.map(prestacion => {
                    const misSesiones = sesiones.filter(s => s.prestacionId === prestacion.id);
                    // Legacy support: use notes directly from prestacion if no sessions found
                    const hasLegacyNotes = (!misSesiones || misSesiones.length === 0) && (prestacion.notasClinicas && prestacion.notasClinicas.length > 0);

                    return (
                        <Card key={prestacion.id} elevation={3}>
                            <CardContent>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div>
                                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#0C234C' }}>
                                            {prestacion.tratamiento} {prestacion.tipo}
                                        </Typography>
                                        <Typography variant="caption">
                                            Creado: {formatDate(prestacion.fechaFin || prestacion.fechaInicio || new Date())} {/* Fallback date */}
                                        </Typography>
                                    </div>
                                    <Chip
                                        label={prestacion.estado}
                                        style={{ backgroundColor: prestacion.estado === 'TERMINADO' ? '#4caf50' : '#ff9800', color: 'white' }}
                                        size="small"
                                    />
                                </div>
                                <Typography variant="subtitle2" gutterBottom>
                                    Dientes: {prestacion.dientes && prestacion.dientes.length > 0 ? prestacion.dientes.join(', ') : 'General'}
                                </Typography>
                                <Divider style={{ margin: '10px 0' }} />

                                <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>Historial de Sesiones:</Typography>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {misSesiones.map(sesion => (
                                        <div key={sesion.id} style={{ fontSize: '0.9rem', padding: '5px', borderBottom: '1px solid #eee' }}>
                                            <span style={{ fontWeight: 'bold', color: '#555' }}>{formatDate(sesion.fecha)}:</span> {sesion.nota}
                                        </div>
                                    ))}
                                    {hasLegacyNotes && prestacion.notasClinicas.map((nota, idx) => (
                                        <div key={idx} style={{ fontSize: '0.9rem', padding: '5px', borderBottom: '1px solid #eee' }}>
                                            <span style={{ fontWeight: 'bold', color: '#555' }}>{formatDate(nota.fecha)}:</span> {nota.nota}
                                        </div>
                                    ))}
                                    {misSesiones.length === 0 && !hasLegacyNotes && (
                                        <Typography variant="body2" color="textSecondary">Sin registros de sesión.</Typography>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        );
    };

    const renderByDiente = () => {
        // Group by tooth
        const toothMap = {};
        const filteredPrestaciones = prestaciones.filter(p => p.estado === 'ACTIVO' || p.estado === 'TERMINADO');

        filteredPrestaciones.forEach(p => {
            if (p.dientes && p.dientes.length > 0) {
                p.dientes.forEach(diente => {
                    if (!toothMap[diente]) toothMap[diente] = [];
                    toothMap[diente].push(p);
                });
            } else {
                // General treatments go to "General" key? Or ignored in tooth view?
                // Let's add them to "Tratamientos Generales"
                if (!toothMap['General']) toothMap['General'] = [];
                toothMap['General'].push(p);
            }
        });

        const sortedTeeth = Object.keys(toothMap).sort((a, b) => {
            if (a === 'General') return -1;
            if (b === 'General') return 1;
            return Number(a) - Number(b);
        });

        if (sortedTeeth.length === 0) return <Typography>No hay registros dentales.</Typography>;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {sortedTeeth.map(diente => (
                    <Card key={diente} elevation={3}>
                        <CardContent>
                            <Typography variant="h5" style={{ color: '#0c8de3', marginBottom: '10px' }}>
                                {diente === 'General' ? 'Tratamientos Generales' : `Pieza Dental ${diente}`}
                            </Typography>
                            <Divider />
                            <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {toothMap[diente].map(p => (
                                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                        <div>
                                            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>{p.tratamiento} {p.tipo}</Typography>
                                            <Typography variant="caption">{p.estado}</Typography>
                                        </div>
                                        <Typography variant="body2" style={{ alignSelf: 'center' }}>
                                            {formatDate(p.fechaFin)}
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <MainContainer>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </MainContainer>
        );
    }

    if (error) return <h1>{`Error: ${error}`}</h1>;

    return (
        <React.Fragment>

            <MainContainer>
                <ContainerBody>
                    <Paper square style={{ marginBottom: '20px' }}>
                        <Tabs
                            value={viewMode}
                            indicatorColor="primary"
                            textColor="primary"
                            onChange={handleChangeTab}
                            centered
                        >
                            <Tab icon={<EventNoteIcon />} label="Por Atenciones" />
                            <Tab icon={<DescriptionIcon />} label="Por Prestación" />
                            <Tab icon={<ViewWeekIcon />} label="Por Diente" />
                        </Tabs>
                    </Paper>

                    <TabPanel value={viewMode} index={0}>
                        {renderByAtencion()}
                    </TabPanel>
                    <TabPanel value={viewMode} index={1}>
                        {renderByPrestacion()}
                    </TabPanel>
                    <TabPanel value={viewMode} index={2}>
                        {renderByDiente()}
                    </TabPanel>

                </ContainerBody>
            </MainContainer>
        </React.Fragment>
    );
};
